import type { KitResponse, KitRequest, SaveKitRequest, SavedKit, Route, PublicUser, UserGearItem, ComparisonItem, ProductComparison } from '@nfa/types';

// Use nullish coalescing to allow empty string (for relative URLs via Vite proxy)
const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'https://api.notfuckingabout.com';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function fetchKit(request: KitRequest): Promise<KitResponse> {
  const response = await fetch(`${API_BASE_URL}/api/kit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.error || 'Failed to fetch kit recommendations');
  }

  return response.json();
}

export async function healthCheck(): Promise<{ status: string }> {
  const response = await fetch(`${API_BASE_URL}/health`);

  if (!response.ok) {
    throw new ApiError(response.status, 'API health check failed');
  }

  return response.json();
}

/**
 * Save a kit configuration and return a short ID for sharing
 */
export async function saveKit(request: SaveKitRequest): Promise<{ id: string }> {
  const response = await fetch(`${API_BASE_URL}/api/kit/save`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to save kit');
  }

  return response.json();
}

/**
 * Retrieve a saved kit by ID
 */
export async function getKitById(id: string): Promise<SavedKit> {
  const response = await fetch(`${API_BASE_URL}/api/kit/${id}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new ApiError(404, 'Kit not found');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to retrieve kit');
  }

  return response.json();
}

// Route types with slug extension
export interface RouteWithSlug extends Route {
  slug: string;
}

export interface RoutesResponse {
  routes: RouteWithSlug[];
  total: number;
  hasMore: boolean;
  filters_applied: {
    country: string | null;
    terrain: string | null;
  };
}

export interface RouteDetailResponse {
  route: RouteWithSlug;
  kit_planning_hint: string;
}

export interface FetchRoutesParams {
  country?: string;
  terrain?: string;
  limit?: number;
  offset?: number;
}

/**
 * Fetch routes with optional filtering and pagination
 */
export async function fetchRoutes(params?: FetchRoutesParams): Promise<RoutesResponse> {
  const searchParams = new URLSearchParams();
  if (params?.country) searchParams.set('country', params.country);
  if (params?.terrain) searchParams.set('terrain', params.terrain);
  if (params?.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params?.offset !== undefined) searchParams.set('offset', String(params.offset));

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/routes${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to fetch routes');
  }

  return response.json();
}

/**
 * Fetch a single route by ID
 * Includes full_gpx=true to ensure GPX data is returned for map display
 */
export async function fetchRouteById(id: string): Promise<RouteDetailResponse> {
  const response = await fetch(`${API_BASE_URL}/api/routes/${id}?full_gpx=true`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new ApiError(404, 'Route not found');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to fetch route');
  }

  return response.json();
}

// ============ Auth API ============

export interface LoginResponse {
  success: boolean;
  message: string;
}

export interface VerifyResponse {
  success: boolean;
  session_token: string;
  user: PublicUser;
}

export interface MeResponse {
  user: PublicUser;
}

/**
 * Request a magic link for authentication
 */
export async function login(email: string): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to send magic link');
  }

  return response.json();
}

/**
 * Verify a magic link token
 */
export async function verifyToken(token: string): Promise<VerifyResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/verify?token=${encodeURIComponent(token)}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Invalid or expired token');
  }

  return response.json();
}

/**
 * Get current user info
 */
export async function getMe(sessionToken: string): Promise<MeResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
    headers: {
      'Authorization': `Bearer ${sessionToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to get user info');
  }

  return response.json();
}

/**
 * Update current user info
 */
export async function updateMe(sessionToken: string, data: { name?: string }): Promise<MeResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${sessionToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to update user');
  }

  return response.json();
}

/**
 * Logout and invalidate session
 */
export async function logout(sessionToken: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${sessionToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to logout');
  }
}

// ============ Gear Closet API ============

export interface GearListResponse {
  items: UserGearItem[];
}

export interface AddGearRequest {
  product_id: string;
  category_id: string;
  product_name: string;
  notes?: string;
  acquired_date?: string;
}

export interface CheckOwnedResponse {
  owned: Record<string, boolean>;
}

/**
 * Get user's gear closet items
 */
export async function getGear(sessionToken: string): Promise<GearListResponse> {
  const response = await fetch(`${API_BASE_URL}/api/gear`, {
    headers: {
      'Authorization': `Bearer ${sessionToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to get gear');
  }

  return response.json();
}

/**
 * Add item to gear closet
 */
export async function addGear(
  sessionToken: string,
  data: AddGearRequest
): Promise<{ item: UserGearItem }> {
  const response = await fetch(`${API_BASE_URL}/api/gear`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${sessionToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to add gear');
  }

  return response.json();
}

/**
 * Remove item from gear closet
 */
export async function removeGear(sessionToken: string, itemId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/gear/${itemId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${sessionToken}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to remove gear');
  }
}

/**
 * Update gear item
 */
export async function updateGear(
  sessionToken: string,
  itemId: string,
  data: { notes?: string; acquired_date?: string }
): Promise<{ item: UserGearItem }> {
  const response = await fetch(`${API_BASE_URL}/api/gear/${itemId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${sessionToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to update gear');
  }

  return response.json();
}

/**
 * Check which products user owns
 */
export async function checkOwnedGear(
  sessionToken: string,
  productIds: string[]
): Promise<CheckOwnedResponse> {
  const response = await fetch(`${API_BASE_URL}/api/gear/check`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${sessionToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ product_ids: productIds }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to check owned gear');
  }

  return response.json();
}

// ============ Category API ============

export interface CategoryProduct {
  id: string;
  name: string;
  tier: 'budget' | 'mid' | 'premium';
  style: 'ultralight' | 'comfort' | 'standard' | null;
  price_gbp: number;
  weight_g: number;
  why: string;
}

export interface CategoryData {
  id: string;
  name: string;
  slug: string;
  description?: string;
  meta_title?: string;
  meta_description?: string;
}

export interface CategoryResponse {
  category: CategoryData;
  products: CategoryProduct[];
  meta: {
    total_products: number;
    filters: {
      tier?: string;
      style?: string;
      sort?: string;
    };
  };
}

export interface FetchCategoryParams {
  slug: string;
  tier?: string;
  style?: string;
  sort?: string;
}

/**
 * Fetch a category with its products, supporting filters
 */
export async function fetchCategory(params: FetchCategoryParams): Promise<CategoryResponse> {
  const searchParams = new URLSearchParams();
  if (params.tier) searchParams.set('tier', params.tier);
  if (params.style) searchParams.set('style', params.style);
  if (params.sort) searchParams.set('sort', params.sort);

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/categories/${params.slug}${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url);

  if (!response.ok) {
    if (response.status === 404) {
      throw new ApiError(404, 'Category not found');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to fetch category');
  }

  return response.json();
}

// ============ Shakedown API ============

/**
 * Parsed gear item from user input
 */
export interface ParsedGearItem {
  name: string;
  weight_g?: number;
  quantity?: number;
  category_hint?: string;
}

/**
 * Product from NFA database (subset of fields)
 */
export interface ShakedownProduct {
  id: string;
  category_id: string;
  name: string;
  tier: 'budget' | 'mid' | 'premium';
  style: 'ultralight' | 'comfort' | 'standard' | null;
  price_gbp: number;
  weight_g: number;
  why: string;
  image_url?: string | null;
  image_alt?: string | null;
  cf_image_id?: string | null;
}

/**
 * A matched gear item with product from NFA database
 */
export interface GearMatch {
  parsed_item: ParsedGearItem;
  matched_product?: ShakedownProduct;
  confidence: number;
  weight_difference_g?: number;
  /** Category inferred from item name using keyword matching */
  inferred_category?: string;
  /** Reason why the item couldn't be matched */
  unmatched_reason?: 'low_confidence' | 'no_category_match' | 'not_in_database';
  /** Suggested alternatives when no match found */
  suggested_alternatives?: ShakedownProduct[];
}

/**
 * Optimization suggestion for a gear item
 */
export interface OptimizationSuggestion {
  product: ShakedownProduct;
  weight_saving_g: number;
  price_difference: number;
  pounds_per_gram: number;
  value_rating: 'excellent' | 'good' | 'moderate' | 'poor';
}

/**
 * Optimization result for a single item
 */
export interface ShakedownOptimization {
  current_item: ParsedGearItem;
  current_product?: ShakedownProduct;
  suggestions: OptimizationSuggestion[];
}

/**
 * Missing essential item detection result
 */
export interface MissingEssential {
  category_id: string;
  category_name: string;
  reason: string;
  importance: 'critical' | 'recommended' | 'optional';
}

/**
 * Shakedown analysis context
 */
export interface ShakedownContext {
  activity?: string;
  accommodation?: string;
  conditions?: {
    precipitation?: string;
    temperature_range?: string;
    midges?: boolean;
  };
}

/**
 * Shakedown result totals
 */
export interface ShakedownTotals {
  total_weight_g: number;
  item_count: number;
  matched_count: number;
  match_rate: number;
}

/**
 * Complete shakedown analysis result
 */
export interface ShakedownResult {
  parsed_items: ParsedGearItem[];
  matches: GearMatch[];
  optimizations: ShakedownOptimization[];
  missing_essentials: MissingEssential[];
  totals: ShakedownTotals;
}

/**
 * Shakedown input types
 */
export type ShakedownInputType = 'text' | 'csv' | 'lighterpack';

/**
 * Analyze shakedown request
 */
export interface AnalyzeShakedownRequest {
  type: ShakedownInputType;
  content: string;
  context?: ShakedownContext;
}

/**
 * Save shakedown response
 */
export interface SaveShakedownResponse {
  slug: string;
  url: string;
}

/**
 * Saved shakedown data
 */
export interface SavedShakedown {
  slug: string;
  input_type: ShakedownInputType;
  content: string;
  context?: ShakedownContext;
  result: ShakedownResult;
  view_count: number;
  created_at: string;
}

/**
 * Analyze a gear list for shakedown
 */
export async function analyzeShakedown(request: AnalyzeShakedownRequest): Promise<ShakedownResult> {
  const response = await fetch(`${API_BASE_URL}/api/shakedown`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to analyze gear list');
  }

  return response.json();
}

/**
 * Save shakedown results for sharing
 */
export async function saveShakedown(
  input_type: ShakedownInputType,
  content: string,
  result: ShakedownResult,
  context?: ShakedownContext
): Promise<SaveShakedownResponse> {
  const response = await fetch(`${API_BASE_URL}/api/shakedown/save`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      input_type,
      content,
      context,
      result,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to save shakedown');
  }

  return response.json();
}

/**
 * Retrieve a saved shakedown by slug
 */
export async function getShakedown(slug: string): Promise<SavedShakedown> {
  const response = await fetch(`${API_BASE_URL}/api/shakedown/${slug}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new ApiError(404, 'Shakedown not found');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to retrieve shakedown');
  }

  return response.json();
}

// ============ Best For API ============

export interface Activity {
  id: string;
  name: string;
  description?: string;
  image_url?: string;
  image_alt?: string;
  cf_image_id?: string;
}

export interface ActivitiesResponse {
  activities: Activity[];
}

/**
 * Fetch list of all supported activities
 */
export async function fetchActivities(): Promise<ActivitiesResponse> {
  const response = await fetch(`${API_BASE_URL}/api/best-for`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to fetch activities');
  }

  return response.json();
}

export interface BestForProduct {
  id: string;
  name: string;
  tier: 'budget' | 'mid' | 'premium';
  price_gbp: number;
  weight_g: number;
  why: string;
  image_url?: string | null;
  image_alt?: string | null;
  cf_image_id?: string | null;
}

export interface BestForCategory {
  id: string;
  name: string;
  products: BestForProduct[];
}

export interface BestForResponse {
  activity: string;
  title: string;
  intro_html: string;
  categories: BestForCategory[];
  outro_html: string;
}

/**
 * Fetch best gear recommendations for an activity
 */
export async function fetchBestFor(activity: string): Promise<BestForResponse> {
  const response = await fetch(`${API_BASE_URL}/api/best-for/${encodeURIComponent(activity)}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new ApiError(404, 'Activity not found');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to fetch best-for recommendations');
  }

  return response.json();
}

// ============ History API ============

/**
 * History kit item from API
 */
export interface HistoryKitItem {
  id: string;
  kit_id: string;
  slug: string | null;
  query: string;
  item_count: number;
  total_weight_kg: number;
  total_price_gbp: number;
  created_at: string;
}

/**
 * History response with pagination
 */
export interface HistoryResponse {
  kits: HistoryKitItem[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * History fetch parameters
 */
export interface FetchHistoryParams {
  limit?: number;
  offset?: number;
}

/**
 * Fetch user's trip history with pagination
 */
export async function fetchHistory(
  sessionToken: string,
  params?: FetchHistoryParams
): Promise<HistoryResponse> {
  const searchParams = new URLSearchParams();
  if (params?.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params?.offset !== undefined) searchParams.set('offset', String(params.offset));

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/history${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${sessionToken}`,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new ApiError(401, 'Authentication required');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to fetch history');
  }

  return response.json();
}

// ============ Comparison API ============

/**
 * Calculated differences between compared items
 */
export interface ComparisonDifferences {
  price_range: { min: number; max: number } | null;
  weight_range: { min: number; max: number } | null;
  price_difference: number;
  weight_difference: number;
  lightest_id: string | null;
  cheapest_id: string | null;
}

/**
 * Full comparison response from API
 */
export interface ComparisonResponse extends ProductComparison {
  differences: ComparisonDifferences;
}

/**
 * Create a new product comparison
 */
export async function createComparison(productIds: string[]): Promise<ComparisonResponse> {
  const response = await fetch(`${API_BASE_URL}/api/comparison`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ product_ids: productIds }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to create comparison');
  }

  return response.json();
}

/**
 * Retrieve a comparison by ID or slug
 */
export async function getComparison(idOrSlug: string): Promise<ComparisonResponse> {
  const response = await fetch(`${API_BASE_URL}/api/comparison/${encodeURIComponent(idOrSlug)}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new ApiError(404, 'Comparison not found');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to fetch comparison');
  }

  return response.json();
}

// Re-export comparison types for convenience
export type { ComparisonItem, ProductComparison };

// ============ Product Detail API ============

/**
 * Full product detail response from API
 */
export interface ProductDetailResponse {
  product: {
    id: string;
    category_id: string;
    category_name: string;
    name: string;
    tier: 'budget' | 'mid' | 'premium';
    style: 'ultralight' | 'comfort' | 'standard' | null;
    fit: 'unisex' | 'mens' | 'womens' | null;
    price_gbp: number;
    weight_g: number;
    why: string;
    description?: string;
    use_cases?: string[];
    limitations?: string[];
    materials?: string[];
    properties?: ProductProperties;
    affiliate_links?: AffiliateLinks;
    image_url?: string | null;
    image_alt?: string | null;
    cf_image_id?: string | null;
  };
}

/**
 * Product properties (1-10 scale ratings)
 */
export interface ProductProperties {
  warmth?: number;
  durability?: number;
  packability?: number;
  versatility?: number;
  weather_resistance?: number;
  weight_efficiency?: number;
  comfort?: number;
  stability?: number;
  traction?: number;
  breathability?: number;
  setup_ease?: number;
  wind_resistance?: number;
}

/**
 * Affiliate links for product
 */
export interface AffiliateLinks {
  amazon?: string;
  cotswold?: string;
  alpkit?: string;
  decathlon?: string;
  rab?: string;
  [retailer: string]: string | undefined;
}

/**
 * Fetch full product details by ID
 */
export async function fetchProductDetail(productId: string): Promise<ProductDetailResponse> {
  const response = await fetch(`${API_BASE_URL}/api/products/${encodeURIComponent(productId)}`);

  if (!response.ok) {
    if (response.status === 404) {
      throw new ApiError(404, 'Product not found');
    }
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to fetch product details');
  }

  return response.json();
}

// ============ Product Search API ============

/**
 * Product search result
 */
export interface ProductSearchResult {
  id: string;
  name: string;
  category_id: string;
  category_name: string;
  tier: 'budget' | 'mid' | 'premium';
  price_gbp: number;
  weight_g: number;
  /** Cloudflare Images CDN ID (preferred) */
  cf_image_id?: string | null;
  /** External product image URL (fallback) */
  image_url?: string | null;
  /** Alt text for accessibility */
  image_alt?: string | null;
}

/**
 * Search products response
 */
export interface ProductSearchResponse {
  products: ProductSearchResult[];
  total: number;
}

/**
 * Search products by query string
 */
export async function searchProducts(
  query: string,
  options?: { limit?: number; categoryId?: string }
): Promise<ProductSearchResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('q', query);
  if (options?.limit) searchParams.set('limit', String(options.limit));
  if (options?.categoryId) searchParams.set('category', options.categoryId);

  const response = await fetch(`${API_BASE_URL}/api/products/search?${searchParams}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new ApiError(response.status, error.message || 'Failed to search products');
  }

  return response.json();
}

// ============ Image Proxy ============

/**
 * Convert an external image URL to a proxied URL that bypasses CORS restrictions.
 * The proxy is served through our API at /api/images/external
 *
 * @param imageUrl - The original external image URL
 * @returns The proxied URL, or the original URL if it's a relative/same-origin URL
 */
export function getProxiedImageUrl(imageUrl: string | undefined | null): string | null {
  if (!imageUrl) {
    return null;
  }

  // Don't proxy relative URLs or same-origin URLs
  if (imageUrl.startsWith('/') || imageUrl.startsWith('data:')) {
    return imageUrl;
  }

  // Don't proxy URLs that are already going through our API
  if (imageUrl.includes('api.notfuckingabout.com') || imageUrl.includes('/api/images')) {
    return imageUrl;
  }

  // Proxy external URLs through our API
  return `${API_BASE_URL}/api/images/external?url=${encodeURIComponent(imageUrl)}`;
}

/**
 * Image variant types for Cloudflare Images
 *
 * NOTE: Custom variants ('thumbnail', 'card') must be created in the
 * Cloudflare Images dashboard before they can be used.
 * Until then, 'public' is used as the fallback.
 *
 * To create variants in Cloudflare dashboard:
 * 1. Go to Images > Variants
 * 2. Create 'thumbnail': 200x200, cover, 80% quality
 * 3. Create 'card': 400x300, cover, 85% quality
 */
export type ImageVariant = 'public' | 'thumbnail' | 'card';

// Map requested variants to available Cloudflare Images variants
// These must exist in CF dashboard: Images > Variants
const VARIANT_FALLBACK: Record<ImageVariant, string> = {
  'public': 'public',     // 1366x768
  'thumbnail': 'thumbnail', // 200x200
  'card': 'card',         // 400x300
};

/**
 * Get the best available image URL for a product.
 *
 * Priority:
 * 1. Cloudflare Images CDN (if cf_image_id is available) - fastest, most reliable
 * 2. External URL via proxy (fallback)
 *
 * @param cfImageId - Cloudflare Images ID (if migrated)
 * @param externalUrl - External image URL (fallback)
 * @param variant - Image variant for CF Images ('public', 'thumbnail', 'card')
 * @returns The best available image URL, or null if no image
 */
export function getProductImageUrl(
  cfImageId: string | null | undefined,
  externalUrl: string | null | undefined,
  variant: ImageVariant = 'public'
): string | null {
  // Prefer Cloudflare Images CDN
  if (cfImageId) {
    // Use fallback variant until custom variants are created in CF dashboard
    const resolvedVariant = VARIANT_FALLBACK[variant] || 'public';
    return `${API_BASE_URL}/api/images/cf/${cfImageId}/${resolvedVariant}`;
  }

  // Fall back to external URL proxy
  if (externalUrl) {
    return getProxiedImageUrl(externalUrl);
  }

  return null;
}

/**
 * Route image variant types
 * Extends base ImageVariant with route-specific variants
 */
export type RouteImageVariant = ImageVariant | 'hero' | 'gallery';

/**
 * Map route-specific variants to available CF Images variants
 * 'hero' and 'gallery' map to appropriate sizes
 */
const ROUTE_VARIANT_MAP: Record<RouteImageVariant, string> = {
  'public': 'public',     // Full size (1366x768)
  'thumbnail': 'thumbnail', // 200x200
  'card': 'card',         // 400x300
  'hero': 'public',       // Hero uses full size
  'gallery': 'card',      // Gallery thumbnails use card size
};

/**
 * Get the best available image URL for a route.
 *
 * Priority:
 * 1. Cloudflare Images CDN (if cf_image_id is available) - fastest, most reliable
 * 2. External URL via proxy (fallback for Unsplash images)
 *
 * @param cfImageId - Cloudflare Images ID (if migrated)
 * @param externalUrl - External image URL (typically Unsplash)
 * @param variant - Image variant ('public', 'thumbnail', 'card', 'hero', 'gallery')
 * @returns The best available image URL, or null if no image
 */
export function getRouteImageUrl(
  cfImageId: string | null | undefined,
  externalUrl: string | null | undefined,
  variant: RouteImageVariant = 'public'
): string | null {
  // Prefer Cloudflare Images CDN
  if (cfImageId && cfImageId.trim() !== '') {
    const resolvedVariant = ROUTE_VARIANT_MAP[variant] || 'public';
    return `${API_BASE_URL}/api/images/cf/${cfImageId}/${resolvedVariant}`;
  }

  // Fall back to external URL proxy
  if (externalUrl) {
    return getProxiedImageUrl(externalUrl);
  }

  return null;
}
